import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/*
  Things I Love — Single‑file React App (JSX)
  Converted from TSX -> plain JS/JSX for simple bundling with esbuild
*/

// ===== Palette (from your spec) =====
const PALETTE = {
  jet: { DEFAULT: "#2d2d2a", 100: "#090908", 200: "#121211", 300: "#1b1b19", 400: "#242422", 500: "#2d2d2a", 600: "#585853", 700: "#84847c", 800: "#adada8", 900: "#d6d6d3" },
  amethyst: { DEFAULT: "#8963ba", 100: "#1b1128", 200: "#36234f", 300: "#513477", 400: "#6c469f", 500: "#8963ba", 600: "#a182c8", 700: "#b8a1d6", 800: "#d0c1e4", 900: "#e7e0f1" },
  davys_gray: { DEFAULT: "#4c4c47", 100: "#0f0f0e", 200: "#1e1e1d", 300: "#2e2e2b", 400: "#3d3d39", 500: "#4c4c47", 600: "#71716b", 700: "#96968f", 800: "#b9b9b4", 900: "#dcdcda" },
  pearl: { DEFAULT: "#e5dcc5", 100: "#3b321b", 200: "#766435", 300: "#b19550", 400: "#cbb98c", 500: "#e5dcc5", 600: "#ebe4d2", 700: "#f0ebdd", 800: "#f5f1e8", 900: "#faf8f4" },
  aquamarine: { DEFAULT: "#b5fed9", 100: "#01552b", 200: "#03ab57", 300: "#09fb82", 400: "#5efdad", 500: "#b5fed9", 600: "#c3fee0", 700: "#d2fee8", 800: "#e1fff0", 900: "#f0fff7" },
};

const DEFAULT_CARD_COLORS = ["amethyst","davys_gray","pearl","aquamarine"];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

// Map palette token → hex
function tokenToHex(token) {
  if (!token) return undefined;
  const hexMatch = token.replace(/^#/, "");
  if (/^[0-9a-fA-F]{6}$/.test(hexMatch)) return `#${hexMatch}`;
  const [key, shadeRaw] = token.split("/");
  const palette = PALETTE[key];
  if (!palette) return undefined;
  if (!shadeRaw) return palette.DEFAULT;
  const shade = Number(shadeRaw);
  return palette[shade] ?? palette.DEFAULT;
}

const Square = ({ className, children, ...rest }) => (
  <div className={classNames("relative aspect-square w-full overflow-hidden rounded-2xl shadow-sm", className)} {...rest}>
    {children}
  </div>
);

const Modal = ({ open, onClose, title, children }) => {
  // Prevent background scroll when modal is open and ensure modal overlays everything.
  // Uses a very large z-index and locks body scrolling for mobile so content isn't cropped.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    document.body.style.overflow = "hidden";
    // Keep position to avoid jump on iOS
    document.body.style.position = prevPosition || "relative";
    return () => {
      document.body.style.overflow = prevOverflow || "";
      document.body.style.position = prevPosition || "";
    };
  }, [open]);

  if (!open) return null;
  const modal = (
    <div
      className="fixed inset-0 grid place-items-center p-4"
      style={{
        backgroundColor: "#000000cc",
        zIndex: 2147483647,
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        WebkitOverflowScrolling: "touch",
        pointerEvents: "auto",
      }}
      role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-xl rounded-2xl p-5 shadow-xl"
        style={{
          position: "relative",
          zIndex: 2147483648,
          backgroundColor: PALETTE.jet[400],
          color: PALETTE.pearl[900],
          boxSizing: "border-box",
          maxHeight: "calc(100vh - 3.5rem)",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
        }}
      >
        <div className="mb-3 flex items-start justify-between gap-4">
          <h3 className="text-xl font-semibold leading-tight" style={{ wordBreak: "break-word" }}>{title}</h3>
          <button
            onClick={onClose}
            className="rounded-full p-2 transition"
            style={{ backgroundColor: "transparent", zIndex: 2147483649 }}
            aria-label="Close"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        <div className="prose max-w-none" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
          {children}
        </div>
      </div>
    </div>
  );
  // Render into document.body to escape any stacking context and ensure it appears above everything.
  return typeof document !== "undefined" && document.body ? createPortal(modal, document.body) : modal;
};

const ThingCard = ({ thing, onClick, active, colorOverride }) => {
  const bg = tokenToHex(thing.color ?? colorOverride) ?? PALETTE.jet[500];
  const fg = PALETTE.pearl[900];
  return (
    <Square
      onClick={onClick}
      style={{ backgroundColor: bg, color: fg, borderColor: PALETTE.davys_gray[400] }}
      className={classNames(
        "cursor-pointer select-none border transition",
      )}
    >
      <div className="absolute inset-0 p-4">
        <div className="flex h-full w-full flex-col items-center justify-center text-center">
          {thing.emoji && <div className="mb-2 text-4xl" aria-hidden="true">{thing.emoji}</div>}
          <div className="line-clamp-2 text-balance text-lg font-semibold">
            {thing.title}
          </div>

          {active && (
            <div className="mt-3 prose max-w-none" style={{ color: PALETTE.davys_gray[700], backgroundColor: "rgba(0,0,0,0.05)", padding: "0.75rem 0.9rem", borderRadius: 10 }}>
              <p style={{ margin: 0 }}>{thing.details}</p>
            </div>
          )}
        </div>
      </div>
    </Square>
  );
};

// ---- Data utils ----
function normalizeThings(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => ({
    title: String(x?.title ?? ""),
    details: String(x?.details ?? ""),
    emoji: x?.emoji,
    color: x?.color,
  })).filter(t => t.title && t.details);
}

function resolveDataUrl() {
  // Allow runtime override
  const w = (typeof window !== "undefined" ? window : {});
  if (w.THINGS_URL) return w.THINGS_URL;
  // Try PUBLIC_URL if present (Create React App)
  const pub = (typeof process !== "undefined" ? process.env?.PUBLIC_URL : undefined);
  if (pub) return `${pub.replace(/\/$/, "")}/things.json`;
  return "/things.json"; // default
}

export default function ThingsILoveApp() {
  const [things, setThings] = useState([]);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);
  const [modalThing, setModalThing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = resolveDataUrl();
    fetch(url)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .then((data) => setThings(normalizeThings(data)))
      .catch((err) => {
        console.error("Failed to load things.json", err);
        setError("Couldn't load things.json");
        setThings([]);
      });
  }, []);

  function handleClickThing(thing) {
    // Always open a modal overlay so desktop and mobile behave the same
    setModalThing(thing);
    // ensure inline expansion stays collapsed
    setActive(null);
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return things;
    return things.filter(t =>
      t.title.toLowerCase().includes(q) || t.details.toLowerCase().includes(q)
    );
  }, [things, query]);

  return (
    <div className="min-h-dvh" style={{ background: `linear-gradient(180deg, ${PALETTE.jet[500]} 0%, ${PALETTE.jet[200]} 100%)`, color: PALETTE.pearl[900] }}>
      <header className="mx-auto max-w-5xl px-4 pt-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              <span>Things I Love About Manogya </span>
            </h1>
          </div>
          <div></div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {error && (
          <div className="mb-4 rounded-2xl border p-4" style={{ borderColor: PALETTE.davys_gray[600], color: PALETTE.davys_gray[700] }}>
            {error}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-10 text-center" style={{ borderColor: PALETTE.davys_gray[600], color: PALETTE.davys_gray[600] }}>
            No matches. Try adjusting your search or JSON.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {filtered.map((thing, i) => (
              <ThingCard key={i} thing={thing} colorOverride={DEFAULT_CARD_COLORS[i % DEFAULT_CARD_COLORS.length]} active={active === thing} onClick={() => handleClickThing(thing)} />
            ))}
          </div>
        )}
      </main>

      <Modal open={!!modalThing} onClose={() => setModalThing(null)} title={modalThing?.title}>
        <p style={{ margin: 0 }}>{modalThing?.details}</p>
      </Modal>

      <footer className="mx-auto max-w-5xl px-4 pb-10 text-center text-xs" style={{ color: PALETTE.davys_gray[700] }}>
        <div className="mt-10">Made by Pedro with love for Manogya</div>
      </footer>

      
    </div>
  );
}

// =====================
// Minimal runtime tests
// =====================
(function runSelfTests() {
  // tokenToHex tests
  console.assert(tokenToHex("#ffffff") === "#ffffff", "hex passthrough failed");
  console.assert(tokenToHex("ffffff") === "#ffffff", "bare hex failed");
  console.assert(tokenToHex("amethyst") === PALETTE.amethyst.DEFAULT, "palette default failed");
  console.assert(tokenToHex("amethyst/700") === PALETTE.amethyst[700], "palette shade failed");
  console.assert(tokenToHex("nope") === undefined, "unknown palette key should be undefined");

  // normalizeThings tests
  const norm = normalizeThings([{ title: "A", details: "B" }, { title: "", details: "x" }, { title: "C" }]);
  console.assert(norm.length === 1 && norm[0].title === "A" && norm[0].details === "B", "normalizeThings failed");
})();
